/**
 * Sidebar navigation. Each entry declares the permission(s) that make it
 * relevant; `Sidebar` renders only the entries the current actor holds at
 * least one of. THIS IS UX ONLY. Hiding "All Tickets" from an end user makes
 * the app make sense to them — it is not what stops them from calling
 * `GET /tickets` directly. Every endpoint behind every one of these routes
 * re-checks authorization and the visibility scope server-side regardless of
 * what this file decides to render (docs/SECURITY.md §1). Never add a route
 * or a data fetch that trusts "the sidebar wouldn't have shown this" as an
 * access-control decision.
 */
import type { ComponentType, SVGProps } from 'react';
import { PERMISSIONS, type Permission } from '@ithelpdesk/shared';
import {
  AllTicketsIcon,
  AwaitingIcon,
  BellIcon,
  ClosedNavIcon,
  DesignWorkflowIcon,
  HomeIcon,
  IncidentReportIcon,
  InvoiceIcon,
  ItChecklistIcon,
  ItChecklistMonitorIcon,
  ResolvedNavIcon,
  UnassignedUserIcon,
  PlusIcon,
  UserIcon,
} from './icons';
import { adminNavItems } from '../features/admin';

export interface NavItem {
  label: string;
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Empty array means "always visible to an authenticated actor" (e.g. Dashboard). Otherwise visible if the actor holds ANY of these. */
  anyOfPermissions: Permission[];
  /** Rendered under the collapsible "Admin" group instead of the main list. */
  group?: 'main' | 'admin';
  /**
   * Visible ALSO when the actor holds `permission` AND the named server-derived
   * flag is true (e.g. Design Workflow: baseline access plus "is a participant"
   * from `GET /design-requests/me`). UX only, like everything in this file.
   */
  visibleWithFlag?: { permission: Permission; flag: NavFlag };
  /** Only highlight on an exact path match (e.g. /tickets must not light up on /tickets/new). */
  end?: boolean;
  /** Plural noun for what the badge counts, announced after the label ("Unassigned, 8 tickets"). A trailing "s" is dropped for a count of 1. */
  countLabel?: string;
}

/**
 * Real counts for nav badges, keyed by `NavItem.to`. Supplied by AppShell
 * from `GET /tickets/counts` (scoped server-side to what the actor may see)
 * and the unread-notification count. A missing key renders no badge — the
 * sidebar never shows a number it wasn't given.
 */
export type NavCounts = Partial<Record<string, number>>;

/** Server-derived facts (not permissions) that make an extra nav item relevant. Supplied by AppShell; a missing flag is false. */
export type NavFlag = 'designWorkflowParticipant';
export type NavFlags = Partial<Record<NavFlag, boolean>>;

/** Any staff role with at least team-scoped or org-wide ticket visibility (TECHNICIAN, TEAM_LEAD, ADMIN, AUDITOR) — not END_USER, who has neither. */
const STAFF_VIEW: Permission[] = [PERMISSIONS.TICKET_READ_TEAM, PERMISSIONS.TICKET_READ_ANY];

export const NAV_ITEMS: NavItem[] = [
  // There is deliberately no "New Ticket" row: creating a ticket stays one
  // step away via the dashboard's Quick Actions, the command palette (Ctrl+K)
  // and the /tickets/new route itself, which is unchanged.
  { label: 'Dashboard', to: '/dashboard', icon: HomeIcon, anyOfPermissions: [] },
  { label: 'All Tickets', to: '/tickets', icon: AllTicketsIcon, anyOfPermissions: STAFF_VIEW, end: true, countLabel: 'open tickets' },
  // Gated on TICKET_READ_OWN alone (not STAFF_VIEW): every role that owns
  // tickets of its own already holds this directly (END_USER, TECHNICIAN,
  // TEAM_LEAD, ADMIN). AUDITOR is the one role that legitimately lacks it —
  // an auditor has no tickets of their own to look at.
  { label: 'My Tickets', to: '/my-tickets', icon: UserIcon, anyOfPermissions: [PERMISSIONS.TICKET_READ_OWN], countLabel: 'open tickets' },
  { label: 'Unassigned', to: '/unassigned', icon: UnassignedUserIcon, anyOfPermissions: STAFF_VIEW, countLabel: 'tickets' },
  { label: 'Awaiting Confirmation', to: '/awaiting-confirmation', icon: AwaitingIcon, anyOfPermissions: STAFF_VIEW, countLabel: 'tickets' },
  { label: 'Resolved', to: '/resolved', icon: ResolvedNavIcon, anyOfPermissions: STAFF_VIEW, countLabel: 'tickets' },
  { label: 'Closed', to: '/closed', icon: ClosedNavIcon, anyOfPermissions: STAFF_VIEW, countLabel: 'tickets' },
  // Every authenticated role has notifications of their own (project brief
  // §21) — always visible, same as Dashboard.
  { label: 'Notifications', to: '/notifications', icon: BellIcon, anyOfPermissions: [], countLabel: 'unread' },
  // Incident Reports are a separate module (not tickets): one row, directly
  // below Notifications and above the Admin group. Shown to anyone who may
  // file or read incident reports; the API re-checks every request.
  {
    label: 'Incident Reports',
    to: '/incidents',
    icon: IncidentReportIcon,
    anyOfPermissions: [PERMISSIONS.INCIDENT_CREATE, PERMISSIONS.INCIDENT_READ_OWN, PERMISSIONS.INCIDENT_READ_INVOLVED, PERMISSIONS.INCIDENT_READ_ALL],
  },
  // IT Invoice Submission: directly below Incident Reports. Only actors holding
  // invoice:view (the IT Administrator by default, or a custom role) see it;
  // every /invoice-submission endpoint re-checks the permission.
  { label: 'IT Invoice Submission', to: '/invoices', icon: InvoiceIcon, anyOfPermissions: [PERMISSIONS.INVOICE_VIEW] },
  // Design Workflow: directly below IT Invoice Submission. Organisation-wide
  // readers (Admin, Auditor) and configurers see it outright; anyone else
  // with baseline access sees it only once `GET /design-requests/me` says
  // they take part in the workflow (a role, a request of their own or an
  // assignment). Every /design-requests endpoint re-checks on the server.
  {
    label: 'Design Workflow',
    to: '/design-workflow',
    icon: DesignWorkflowIcon,
    anyOfPermissions: [PERMISSIONS.DESIGN_REQUEST_READ_ALL, PERMISSIONS.DESIGN_WORKFLOW_CONFIGURE],
    visibleWithFlag: { permission: PERMISSIONS.DESIGN_WORKFLOW_ACCESS, flag: 'designWorkflowParticipant' },
  },
  // IT Checklist: directly below Design Workflow. Anyone holding it_checklist:submit
  // (technicians, team leads, admins) sees it; the API re-checks every request.
  { label: 'IT Checklist', to: '/it-checklist', icon: ItChecklistIcon, anyOfPermissions: [PERMISSIONS.IT_CHECKLIST_SUBMIT] },
  // IT Checklist Monitor: directly below IT Checklist. Organisation-wide view of every
  // engineer's checklist submissions (live feed); only it_checklist:view_all holders
  // (IT Administrator, Auditor by default) see it. Every /it-checklist/monitor
  // endpoint, including the live stream, re-checks the permission and the org.
  {
    label: 'IT Checklist Monitor',
    to: '/it-checklist-monitor',
    icon: ItChecklistMonitorIcon,
    anyOfPermissions: [PERMISSIONS.IT_CHECKLIST_VIEW_ALL],
  },
  ...adminNavItems,
];

/**
 * Not a sidebar row (the sidebar matches the reference layout, which has no
 * "New Ticket" entry) but still one keystroke away: the command palette lists
 * it first for anyone who may create tickets. Same UX-only gating as the nav.
 */
export const NEW_TICKET_ITEM: NavItem = {
  label: 'New Ticket',
  to: '/tickets/new',
  icon: PlusIcon,
  anyOfPermissions: [PERMISSIONS.TICKET_CREATE],
  end: true,
};

/**
 * Everything the command palette can jump to: the sidebar's pages with the
 * create action right after Dashboard (where the old sidebar row sat, so the
 * palette's default ordering is unchanged).
 */
export const PALETTE_ITEMS: NavItem[] = NAV_ITEMS.flatMap((item) => (item.to === '/dashboard' ? [item, NEW_TICKET_ITEM] : [item]));

export function isNavItemVisible(item: NavItem, actorPermissions: Set<Permission>, flags: NavFlags = {}): boolean {
  if (item.anyOfPermissions.length === 0) return true;
  if (item.anyOfPermissions.some((p) => actorPermissions.has(p))) return true;
  const gated = item.visibleWithFlag;
  return gated !== undefined && actorPermissions.has(gated.permission) && flags[gated.flag] === true;
}
